// src/taxConfig.ts
// ─────────────────────────────────────────────────────────────────────────────
// ALL UK TAX RATES LIVE HERE. Nowhere else.
//
// To update for a new tax year:
//   1. Add a new block below (copy the latest year as template)
//   2. Update the values against HMRC published rates (gov.uk/rates-and-allowances)
//   3. Update DEFAULT_TAX_YEAR
//
// Every component that needs tax rates imports from this file.
// Zero hardcoded rates anywhere else in the codebase.
// ─────────────────────────────────────────────────────────────────────────────

export const TAX_YEARS = {

  '2025/26': {
    label: '2025/26',
    startDate: '2025-04-06',

    // ── Income tax ────────────────────────────────────────────────────────────
    personalAllowance:    12570,   // frozen to 2031
    basicRateLimit:       50270,   // top of basic rate band (inclusive)
    higherRateLimit:     125140,   // top of higher rate band (inclusive)
    incomeTaxBasic:       0.20,
    incomeTaxHigher:      0.40,
    incomeTaxAdditional:  0.45,

    // ── Dividend tax ──────────────────────────────────────────────────────────
    dividendAllowance:          500,
    dividendBasicRate:        0.0875,
    dividendHigherRate:       0.3375,
    dividendAdditionalRate:   0.3935,

    // ── National Insurance (employee) ─────────────────────────────────────────
    niPrimaryThreshold:   12570,   // annualised; same as PA since April 2022
    niUpperEarningsLimit: 50270,   // annualised
    niEmployeeRate:        0.08,
    niEmployeeUpperRate:   0.02,

    // ── National Insurance (employer) ─────────────────────────────────────────
    niEmployerThreshold:   5000,   // reduced from £9,100 in April 2025
    niEmployerRate:        0.15,   // increased from 13.8% in April 2025

    // ── Corporation tax ───────────────────────────────────────────────────────
    ctSmallProfitsRate:    0.19,   // profits ≤ £50,000
    ctSmallProfitsLimit:  50000,
    ctMainRate:            0.25,   // profits ≥ £250,000
    ctMainRateLimit:      250000,
    // Marginal relief applies between ctSmallProfitsLimit and ctMainRateLimit
    // Effective marginal rate = 26.5% in that band

    // ── Student loan repayment thresholds (annual) ─────────────────────────
    slPlan1Threshold:      24990,
    slPlan2Threshold:      27295,
    slPlan5Threshold:      25000,
    slPostgradThreshold:   21000,
    slUndergradRate:       0.09,
    slPostgradRate:        0.06,

    // ── Savings & investment allowances ──────────────────────────────────────
    isaAllowance:           20000,
    pensionAnnualAllowance: 60000,
  },

  '2026/27': {
    label: '2026/27',
    startDate: '2026-04-06',

    // ── Income tax — UNCHANGED ────────────────────────────────────────────────
    personalAllowance:    12570,
    basicRateLimit:       50270,
    higherRateLimit:     125140,
    incomeTaxBasic:       0.20,
    incomeTaxHigher:      0.40,
    incomeTaxAdditional:  0.45,

    // ── Dividend tax — INCREASED +2% at basic and higher rate ─────────────────
    // Source: Autumn Budget 2025, Finance (No.2) Bill 2024–26, effective 6 April 2026
    dividendAllowance:          500,
    dividendBasicRate:        0.1075,   // was 0.0875 — CHANGED
    dividendHigherRate:       0.3575,   // was 0.3375 — CHANGED
    dividendAdditionalRate:   0.3935,   // unchanged

    // ── National Insurance — UNCHANGED ────────────────────────────────────────
    niPrimaryThreshold:   12570,
    niUpperEarningsLimit: 50270,
    niEmployeeRate:        0.08,
    niEmployeeUpperRate:   0.02,
    niEmployerThreshold:   5000,
    niEmployerRate:        0.15,

    // ── Corporation tax — UNCHANGED ───────────────────────────────────────────
    ctSmallProfitsRate:    0.19,
    ctSmallProfitsLimit:  50000,
    ctMainRate:            0.25,
    ctMainRateLimit:      250000,

    // ── Student loan repayment thresholds (annual) — UNCHANGED ────────────
    slPlan1Threshold:      24990,
    slPlan2Threshold:      27295,
    slPlan5Threshold:      25000,
    slPostgradThreshold:   21000,
    slUndergradRate:       0.09,
    slPostgradRate:        0.06,

    // ── Savings & investment allowances — UNCHANGED ───────────────────────────
    // Note: cash ISA limit reduces to £12,000 from April 2027, not this year
    isaAllowance:           20000,
    pensionAnnualAllowance: 60000,
  },

} as const;

// ── State pension constants ──────────────────────────────────────────────────
export const STATE_PENSION = {
  annualAmount: 11502,   // Full new state pension 2024/25
  qualifyingAge: 67,     // Current UK state pension age
} as const;

// ─── Pension Access Age ───────────────────────────────────────────────────────
// Minimum pension access age rules (Finance Act 2022).
// IMPORTANT: All thresholds live here — never hardcode in components.
export const PENSION_ACCESS = {
  // Clients who turn 55 before 6 April 2028 retain access at 55
  legacyAccessAge: 55,
  legacyDeadline: new Date(2028, 3, 6), // 6 April 2028
  // Post-2028 minimum access age (legislated)
  post2028AccessAge: 57,
  // Post-2046 minimum access age — PROPOSED (tracks state pension age - 10)
  // NOT YET LEGISLATED — uncomment and add threshold date when confirmed:
  // post2046AccessAge: 58,
  // post2046Deadline: new Date(2046, 3, 6),
} as const;

export type TaxYear = keyof typeof TAX_YEARS;
export type TaxConfig = typeof TAX_YEARS[TaxYear];

export const ALL_TAX_YEARS = Object.keys(TAX_YEARS) as TaxYear[];

// ─────────────────────────────────────────────────────────────────────────────
// Auto-detect the correct tax year from a session date.
// Used so the app stays correct across April without manual intervention.
// ─────────────────────────────────────────────────────────────────────────────
export function getTaxYearForDate(sessionDate: string | Date): TaxYear {
  let d: Date;
  if (typeof sessionDate === 'string') {
    // Handle DD/MM/YYYY format (from toLocaleDateString('en-GB'))
    const parts = sessionDate.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (parts) {
      d = new Date(parseInt(parts[3]), parseInt(parts[2]) - 1, parseInt(parts[1]));
    } else {
      // Fallback: ISO or other format
      d = new Date(sessionDate);
    }
  } else {
    d = sessionDate;
  }
  const year  = d.getFullYear();
  const month = d.getMonth() + 1; // 1-indexed
  const day   = d.getDate();

  // On or after 6 April 2026 → 2026/27
  if (year > 2026 || (year === 2026 && (month > 4 || (month === 4 && day >= 6)))) {
    return '2026/27';
  }
  return '2025/26';
}

export const DEFAULT_TAX_YEAR: TaxYear = '2026/27';

// ─────────────────────────────────────────────────────────────────────────────
// Corporation tax marginal rate calculation (for CT efficiency displays)
// ─────────────────────────────────────────────────────────────────────────────
// Tax-year-aware variant — uses the limits and rates from a specific TaxConfig.
export function effectiveCTRateForYear(annualProfit: number, ty: TaxConfig): number {
  if (annualProfit <= 0) return ty.ctSmallProfitsRate;
  if (annualProfit <= ty.ctSmallProfitsLimit) return ty.ctSmallProfitsRate;
  if (annualProfit >= ty.ctMainRateLimit) return ty.ctMainRate;
  // Marginal relief band: effective rate rises from 19% to 25% across £50k–£250k.
  const marginalRelief = (ty.ctMainRateLimit - annualProfit) * (3 / 200);
  const taxPayable = annualProfit * ty.ctMainRate - marginalRelief;
  return taxPayable / annualProfit;
}

// Standalone variant — canonical entry point for all screens and projections.
// Uses the 2024/25 limits (£50k / £250k) hardcoded; HMRC marginal relief formula
// with fraction 3/200. Returns a decimal (0.19 – 0.25). This is the version
// S2 screens, Scoreboard and projections should import.
export function effectiveCTRate(profit: number): number {
  if (profit <= 50000) return 0.19;
  if (profit >= 250000) return 0.25;
  // Marginal relief: 0.25 − (3/200) × (250000 − profit) / profit
  return 0.25 - (3 / 200) * ((250000 - profit) / profit);
}
